import {
  FileItem,
  ImageGallery,
  LoadingSpinner,
  Modal,
  Video,
} from "@/components";
import { useMarkAsRead } from "@/hooks";
import { MyNotiStatus, NOTIFICATION_QUERY_KEYS } from "@/types";
import { formatDateFromNow } from "@/utils/formatDate";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useGetMyNotificationDetail } from "../hooks";

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  notificationId: number;
};

const DetailMyNotificationModal: React.FC<Props> = ({
  open,
  setOpen,
  notificationId,
}) => {
  const {
    notification,
    isLoading,
    error,
    isFetched,
    images,
    videos,
    documents,
  } = useGetMyNotificationDetail(notificationId);

  const queryClient = useQueryClient();

  const { markAsRead } = useMarkAsRead();

  // Update status to read when opening the modal
  useEffect(() => {
    if (open && notification && notification.status !== MyNotiStatus.READ) {
      // Call API to mark notification as read
      markAsRead([notification.notification_id]);

      // Call api to reset list notification
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATION_QUERY_KEYS.ALL_NOTIFICATIONS],
      });
    }
  }, [open, notification]);

  if (error) throw error;
  if (isFetched && !notification) return <Navigate to="/404" replace />;

  return (
    <Modal
      open={open}
      onOpenChange={setOpen}
      title="通知の詳細"
      isStopPropagationOnOverlay
      contentClassName="rounded-none"
      displayCloseIcon
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="space-y-4">
          <h2 className="font-semibold leading-6 text-light_primary smart-break whitespace-pre-line">
            {notification?.notification?.title}
          </h2>

          <div className="flex items-center space-x-2 text-xs text-light_secondary">
            <div className="line-clamp-1 truncate">
              {notification?.notification?.manager}
            </div>
            <div className="size-[10px] rounded-full bg-[#D9D9D9] shrink-0"></div>
            <div className="shrink-0">
              {notification
                ? formatDateFromNow(notification?.created_at)
                : null}
            </div>
          </div>

          <p className="leading-5.5 text-light_primary text-sm whitespace-pre-wrap break-all">
            {notification?.notification?.description}
          </p>

          <ImageGallery images={images} />

          {videos.map((video) => (
            <Video
              key={video.id}
              url={video.url}
              className="w-full max-h-[400px] rounded-lg bg-black"
            />
          ))}

          {documents.map((document) => (
            <FileItem
              key={document.id}
              url={document.url}
              name={document.name}
              type={document.type}
            />
          ))}
        </div>
      )}
    </Modal>
  );
};

export { DetailMyNotificationModal };
